import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { teacherModuleAPI } from '../../services/api';

function formatChoices(options) {
  const active = (options || []).filter((o) => o.isActive !== false);
  if (!active.length) return '— No choices yet —';
  return active.map((o) => o.label).join(' · ');
}

function flattenCategoryRows(category, showHidden) {
  const rows = [];
  for (const mod of category?.modules || []) {
    if (!showHidden && mod.isActive === false) continue;

    let params = mod.parameters || [];
    if (!showHidden) params = params.filter((p) => p.isActive !== false);

    if (params.length === 0) {
      if ((mod.parameters || []).length > 0 && !showHidden) continue;
      rows.push({
        key: `mod-${mod.id}`,
        moduleId: mod.id,
        moduleName: mod.name,
        moduleActive: mod.isActive,
        parameterId: null,
        parameterName: null,
        parameterActive: true,
        options: [],
        rowSpan: 1,
        isModuleOnly: true,
      });
      continue;
    }

    params.forEach((param, idx) => {
      rows.push({
        key: param.id,
        moduleId: mod.id,
        moduleName: mod.name,
        moduleActive: mod.isActive,
        parameterId: param.id,
        parameterName: param.name,
        parameterActive: param.isActive,
        options: param.options || [],
        rowSpan: idx === 0 ? params.length : 0,
        isModuleOnly: false,
      });
    });
  }
  return rows;
}

export default function AssessmentCatalogAdmin() {
  const queryClient = useQueryClient();
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [showHidden, setShowHidden] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState({});

  const catalogQuery = useQuery(['assessmentCatalogAdmin'], async () => {
    const res = await teacherModuleAPI.getAssessmentCatalogAdmin();
    return res.data?.data || [];
  });

  const categories = useMemo(() => catalogQuery.data || [], [catalogQuery.data]);
  const visibleCategories = useMemo(
    () => (showHidden ? categories : categories.filter((c) => c.isActive !== false)),
    [categories, showHidden]
  );

  useEffect(() => {
    if (!categories.length) {
      setActiveCategoryId(null);
      return;
    }
    const pool = showHidden ? categories : categories.filter((c) => c.isActive !== false);
    const selectable = pool.length ? pool : categories;
    if (!activeCategoryId || !selectable.some((c) => c.id === activeCategoryId)) {
      setActiveCategoryId(selectable[0].id);
    }
  }, [categories, activeCategoryId, showHidden]);

  const activeCategory =
    visibleCategories.find((c) => c.id === activeCategoryId) || visibleCategories[0] || null;
  const tabIndex = Math.max(
    0,
    visibleCategories.findIndex((c) => c.id === activeCategory?.id)
  );

  const tableRows = useMemo(
    () => flattenCategoryRows(activeCategory, showHidden),
    [activeCategory, showHidden]
  );

  const saveMutation = useMutation(
    async () => {
      if (dialog?.type === 'category') {
        if (dialog.mode === 'create') {
          return teacherModuleAPI.createAssessmentCategory({
            name: form.name,
            parentName: form.name,
            sortOrder: form.sortOrder || 999,
          });
        }
        return teacherModuleAPI.updateAssessmentCategory(dialog.id, {
          name: form.name,
          parentName: form.name,
          isActive: form.isActive,
        });
      }
      if (dialog?.type === 'module') {
        if (dialog.mode === 'create') {
          return teacherModuleAPI.createAssessmentModule(form);
        }
        return teacherModuleAPI.updateAssessmentModule(dialog.id, {
          name: form.name,
          parentName: form.name,
          isActive: form.isActive,
        });
      }
      if (dialog?.type === 'parameter') {
        if (dialog.mode === 'create') {
          return teacherModuleAPI.createAssessmentParameter({
            moduleId: form.moduleId,
            name: form.name,
            parentName: form.name,
            sortOrder: form.sortOrder || 999,
          });
        }
        return teacherModuleAPI.updateAssessmentParameter(dialog.id, {
          name: form.name,
          parentName: form.name,
          isActive: form.isActive,
        });
      }
      if (dialog?.type === 'choices') {
        await teacherModuleAPI.updateAssessmentParameter(dialog.parameterId, {
          name: form.parameterName,
          isActive: form.parameterActive,
        });
        for (const opt of form.options || []) {
          if (opt.id && !String(opt.id).startsWith('new-')) {
            await teacherModuleAPI.updateAssessmentOption(opt.id, {
              label: opt.label,
              parentLabel: opt.label,
              helpText: opt.helpText || '',
              isActive: opt.isActive !== false,
              scoreValue: opt.scoreValue ?? 1,
            });
          } else if (opt.label?.trim()) {
            await teacherModuleAPI.createAssessmentOption({
              parameterId: dialog.parameterId,
              label: opt.label.trim(),
              parentLabel: opt.label.trim(),
              helpText: opt.helpText?.trim() || '',
              scoreValue: opt.scoreValue ?? 1,
              sortOrder: opt.sortOrder ?? 999,
            });
          }
        }
        return { success: true };
      }
      return null;
    },
    {
      onSuccess: (res) => {
        toast.success('Saved');
        if (dialog?.type === 'category' && dialog.mode === 'create' && res?.data?.data?.id) {
          setActiveCategoryId(res.data.data.id);
        }
        setDialog(null);
        setForm({});
        queryClient.invalidateQueries(['assessmentCatalogAdmin']);
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Save failed'),
    }
  );

  const openCategoryDialog = (mode, category = null) => {
    if (mode === 'create') {
      setDialog({ type: 'category', mode: 'create' });
      setForm({ name: '' });
      return;
    }
    setDialog({ type: 'category', mode: 'edit', id: category.id });
    setForm({ name: category.name, isActive: category.isActive !== false });
  };

  const openChoicesDialog = (row) => {
    setDialog({ type: 'choices', parameterId: row.parameterId, moduleName: row.moduleName });
    setForm({
      parameterName: row.parameterName,
      parameterActive: row.parameterActive !== false,
      options: (row.options || []).map((o, i) => ({
        id: o.id,
        label: o.label,
        helpText: o.helpText || '',
        isActive: o.isActive !== false,
        scoreValue: o.scoreValue ?? i + 1,
        sortOrder: o.sortOrder ?? i + 1,
      })),
    });
  };

  const openModuleDialog = (row) => {
    setDialog({ type: 'module', mode: 'edit', id: row.moduleId });
    setForm({ name: row.moduleName, isActive: row.moduleActive !== false });
  };

  if (catalogQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Assessment form setup
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        This is what teachers see in student monthly reports. Add subjects (e.g. Blindfold), then
        modules, questions, and answer choices inside each subject tab.
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        Use <strong>Add new subject</strong> for a new top-level section (like Blindfold). Inside a
        subject tab, each table row is one question teachers fill. Click <strong>Edit choices</strong>{' '}
        to change the dropdown answers.
      </Alert>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} mb={2}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openCategoryDialog('create')}>
          Add new subject
        </Button>
        {activeCategory && (
          <Button variant="outlined" onClick={() => openCategoryDialog('edit', activeCategory)}>
            Edit subject
          </Button>
        )}
      </Stack>

      {visibleCategories.length > 0 && (
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setActiveCategoryId(visibleCategories[v]?.id || null)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          {visibleCategories.map((cat) => (
            <Tab
              key={cat.id}
              label={
                cat.isActive === false ? (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <span>{cat.name}</span>
                    <Chip size="small" label="Hidden" color="warning" />
                  </Stack>
                ) : (
                  cat.name
                )
              }
            />
          ))}
        </Tabs>
      )}

      {!visibleCategories.length && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No subjects yet. Click <strong>Add new subject</strong> to create one (e.g. Blindfold).
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} mb={2}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          disabled={!activeCategory}
          onClick={() => {
            setDialog({ type: 'module', mode: 'create', categoryId: activeCategory.id });
            setForm({ categoryId: activeCategory.id, name: '' });
          }}
        >
          Add new module
        </Button>
        <Button
          variant="outlined"
          disabled={!activeCategory?.modules?.length}
          onClick={() => {
            setDialog({ type: 'parameter', mode: 'create' });
            setForm({ moduleId: activeCategory?.modules?.[0]?.id || '', name: '' });
          }}
        >
          Add new question
        </Button>
        <FormControlLabel
          control={
            <Switch checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} />
          }
          label="Show hidden items"
        />
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" sx={{ minWidth: 640 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 700, width: '22%' }}>Module</TableCell>
              <TableCell sx={{ fontWeight: 700, width: '22%' }}>Question</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Answer choices (teacher dropdown)</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 100 }} align="center">
                Visible
              </TableCell>
              <TableCell sx={{ fontWeight: 700, width: 140 }} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableRows.map((row) => {
              const hidden = row.moduleActive === false || row.parameterActive === false;
              return (
                <TableRow
                  key={row.key}
                  sx={{ opacity: hidden ? 0.55 : 1, bgcolor: hidden ? 'grey.50' : undefined }}
                >
                  {row.rowSpan > 0 && (
                    <TableCell
                      rowSpan={row.rowSpan}
                      sx={{ verticalAlign: 'top', fontWeight: 600, borderRight: 1, borderColor: 'divider' }}
                    >
                      {row.moduleName}
                      {row.moduleActive === false && (
                        <Chip size="small" label="Hidden" color="warning" sx={{ ml: 0.5 }} />
                      )}
                      <Box sx={{ mt: 1 }}>
                        <Button size="small" onClick={() => openModuleDialog(row)}>
                          Edit module
                        </Button>
                      </Box>
                    </TableCell>
                  )}
                  {row.isModuleOnly ? (
                    <>
                      <TableCell colSpan={2} sx={{ color: 'text.secondary' }}>
                        No questions yet — use &quot;Add new question&quot;
                      </TableCell>
                      <TableCell align="center">—</TableCell>
                      <TableCell />
                    </>
                  ) : (
                    <>
                      <TableCell>{row.parameterName}</TableCell>
                      <TableCell sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                        {formatChoices(row.options)}
                      </TableCell>
                      <TableCell align="center">
                        {hidden ? (
                          <Chip size="small" label="Hidden" color="warning" />
                        ) : (
                          <Chip size="small" label="Yes" color="success" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="contained" onClick={() => openChoicesDialog(row)}>
                          Edit choices
                        </Button>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              );
            })}
            {!tableRows.length && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  Nothing here yet. Add a module to get started.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {dialog?.type === 'category' && (
        <Dialog open onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
          <DialogTitle>{dialog.mode === 'create' ? 'Add new subject' : 'Edit subject'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Subject name (e.g. Blindfold)"
                fullWidth
                value={form.name || ''}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              {dialog.mode === 'edit' && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.isActive !== false}
                      onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    />
                  }
                  label="Show to teachers"
                />
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialog(null)}>Cancel</Button>
            <Button variant="contained" onClick={() => saveMutation.mutate()} disabled={!form.name?.trim()}>
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {dialog?.type === 'module' && (
        <Dialog open onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
          <DialogTitle>{dialog.mode === 'create' ? 'Add new module' : 'Edit module'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Module name (e.g. Touch and smell)"
                fullWidth
                value={form.name || ''}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              {dialog.mode === 'edit' && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.isActive !== false}
                      onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    />
                  }
                  label="Show to teachers"
                />
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialog(null)}>Cancel</Button>
            <Button variant="contained" onClick={() => saveMutation.mutate()} disabled={!form.name?.trim()}>
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {dialog?.type === 'parameter' && dialog.mode === 'create' && (
        <Dialog open onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Add new question</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Under module</InputLabel>
                <Select
                  label="Under module"
                  value={form.moduleId || ''}
                  onChange={(e) => setForm({ ...form, moduleId: e.target.value })}
                >
                  {(activeCategory?.modules || []).map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Question name (e.g. Inhale time)"
                fullWidth
                value={form.name || ''}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialog(null)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => saveMutation.mutate()}
              disabled={!form.name?.trim() || !form.moduleId}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {dialog?.type === 'choices' && (
        <Dialog open onClose={() => setDialog(null)} maxWidth="md" fullWidth>
          <DialogTitle>Edit answer choices</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {dialog.moduleName} → {form.parameterName}
            </Typography>
            <TextField
              label="Question label"
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              value={form.parameterName || ''}
              onChange={(e) => setForm({ ...form, parameterName: e.target.value })}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.parameterActive !== false}
                  onChange={(e) => setForm({ ...form, parameterActive: e.target.checked })}
                />
              }
              label="Show this question to teachers"
              sx={{ mb: 2, display: 'block' }}
            />
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Dropdown choices
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Optional explanation text is shown to teachers while filling, during review, and on the
              parent report.
            </Typography>
            <Stack spacing={2}>
              {(form.options || []).map((opt, idx) => (
                <Box
                  key={opt.id || idx}
                  sx={{
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    opacity: opt.isActive === false ? 0.6 : 1,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      size="small"
                      fullWidth
                      label="Choice label"
                      placeholder="e.g. 6-8 Sec"
                      value={opt.label}
                      disabled={opt.isActive === false}
                      onChange={(e) => {
                        const options = [...form.options];
                        options[idx] = { ...options[idx], label: e.target.value };
                        setForm({ ...form, options });
                      }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={opt.isActive !== false}
                          onChange={(e) => {
                            const options = [...form.options];
                            options[idx] = { ...options[idx], isActive: e.target.checked };
                            setForm({ ...form, options });
                          }}
                        />
                      }
                      label="Show"
                    />
                  </Stack>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    sx={{ mt: 1 }}
                    label="Explanation (optional)"
                    placeholder="What this choice means — shown as read-only help"
                    value={opt.helpText || ''}
                    disabled={opt.isActive === false}
                    onChange={(e) => {
                      const options = [...form.options];
                      options[idx] = { ...options[idx], helpText: e.target.value };
                      setForm({ ...form, options });
                    }}
                  />
                </Box>
              ))}
            </Stack>
            <Button
              size="small"
              startIcon={<AddIcon />}
              sx={{ mt: 2 }}
              onClick={() =>
                setForm({
                  ...form,
                  options: [
                    ...(form.options || []),
                    {
                      id: `new-${Date.now()}`,
                      label: '',
                      helpText: '',
                      isActive: true,
                      scoreValue: (form.options?.length || 0) + 1,
                    },
                  ],
                })
              }
            >
              Add another choice
            </Button>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialog(null)}>Cancel</Button>
            <Button variant="contained" onClick={() => saveMutation.mutate()}>
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}
